/** Shared pieces of the Apply Leave and Assign Leave forms. */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { fetchOrgHolidays } from "../../../Moduels/leave/leaveApi";
import { fetchWfHolidays } from "../../../Moduels/attendance/wfApi";
import API from "../../../services/api";
import { isLeaveBalanceType } from "./leavePackageCatalog";
import { toDateValue } from "../../common/datePickerUtils";
import { isOrgWeekOffDate } from "../../../Moduels/attendance/attendanceCalendarUtils";
import { formatIndianDate } from "../../../Moduels/attendance/indianCalendarUtils";

export const COMP_OFF_CODE = "CO";

/** Leave types that can be requested: balance types plus Comp Off (when configured). */
export function isApplicableCode(code) {
  return isLeaveBalanceType(code) || String(code || "").toUpperCase() === COMP_OFF_CODE;
}

/** Employment-type and gender applicability of a leave type for an employee. */
export function isLeaveApplicableToEmployee(leaveType, employee) {
  const appJson = leaveType.applicability_json || {};
  const applicable = appJson.applicable_to;
  if (Array.isArray(applicable) && applicable.length > 0) {
    const empType = String(employee?.employee_type || employee?.employment_type || "Permanent").toLowerCase();
    if (!applicable.some((v) => String(v).toLowerCase() === empType)) return false;
  }
  const gender = String(leaveType.gender_restriction || "").toLowerCase();
  const empGender = String(employee?.gender || "").toLowerCase();
  if (gender && empGender && gender !== empGender) return false;
  return true;
}

/** Active, requestable leave types that apply to ``employee``. */
export function requestableLeaveTypes(types, employee) {
  return (types || []).filter(
    (t) => t.is_active !== false && !t.is_archived && isApplicableCode(t.code) && isLeaveApplicableToEmployee(t, employee)
  );
}

export const fmtDays = (n) => {
  const v = Number(n || 0);
  const shown = Number.isInteger(v) ? v : v.toFixed(1);
  return `${shown} day${v === 1 ? "" : "s"}`;
};

/** True when a Comp Off request asks for more days than the employee can use. */
export function compOffExceedsBalance(isCompOff, requested, available) {
  return Boolean(isCompOff) && requested != null && Number(requested) > Number(available || 0);
}

export function CompOffOverBalanceNotice({ available, requested, who = "This employee" }) {
  if (!compOffExceedsBalance(true, requested, available)) return null;
  const shortBy = Number(requested) - Number(available || 0);
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm" role="alert">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
          <AlertTriangle size={16} />
        </div>
        <div>
          <p className="text-sm font-bold text-amber-950">Comp Off balance is not enough</p>
          <p className="mt-1 text-sm leading-relaxed text-amber-900">
            {who} has <strong>{fmtDays(available)}</strong> available. This request is <strong>{fmtDays(requested)}</strong>,
            which is <strong>{fmtDays(shortBy)}</strong> more than the balance. Shorten the dates to {fmtDays(available)} or
            less. The request cannot be submitted until it fits.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Same day-count rule the server uses (calendar days; a half day is 0.5). */
export function leaveDayCount(startDate, endDate, halfDay) {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
  if (halfDay) return 0.5;
  return Math.round((end - start) / 86400000) + 1;
}

/** Days in pending / escalated requests of a leave type (reserved by the server). */
export function pendingDaysFor(requests, leaveTypeId) {
  return (requests || [])
    .filter((r) => r.leave_type_id === leaveTypeId && ["pending", "escalated"].includes(r.status))
    .reduce((sum, r) => sum + Number(r.total_days || 0), 0);
}

/** Calendar markers: week-offs, holidays and the employee's open/approved leave. */
export function useLeaveDayMarkers(orgId, requests) {
  const year = new Date().getFullYear();
  const [holidays, setHolidays] = useState([]);
  const [leaveModuleHolidays, setLeaveModuleHolidays] = useState([]);
  const [weekOffPolicy, setWeekOffPolicy] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    const range = { from_date: `${year - 1}-01-01`, to_date: `${year + 1}-12-31` };
    // Organisation holidays as configured on the Holiday Calendar page (also used by attendance/payroll).
    fetchWfHolidays(range)
      .then((d) => setHolidays(Array.isArray(d) ? d : []))
      .catch(() => setHolidays([]));
    // Leave-module holiday settings (catalog toggles), kept as an additional source.
    fetchOrgHolidays({ organisation_id: orgId, ...range })
      .then((d) => setLeaveModuleHolidays(Array.isArray(d) ? d : []))
      .catch(() => setLeaveModuleHolidays([]));
    API.get("/organisation/me/week-off-policy")
      .then(({ data }) => setWeekOffPolicy(data || {}))
      .catch(() => setWeekOffPolicy(null));
  }, [orgId, year]);

  return useMemo(() => {
    const markers = {};
    if (weekOffPolicy) {
      const end = new Date(year + 1, 11, 31);
      for (let d = new Date(year - 1, 0, 1); d <= end; d.setDate(d.getDate() + 1)) {
        if (isOrgWeekOffDate(d, weekOffPolicy)) markers[toDateValue(d)] = { type: "weekoff", label: "Week Off" };
      }
    }
    for (const h of leaveModuleHolidays) {
      if (h.is_enabled === false) continue;
      const d = String(h.holiday_date || h.date || "").slice(0, 10);
      if (d) markers[d] = { type: "holiday", label: h.custom_name || h.name || h.holiday_name || "Holiday" };
    }
    // Holiday Calendar entries win on the same date.
    for (const h of holidays) {
      const d = String(h.holiday_date || h.date || "").slice(0, 10);
      if (!d) continue;
      const name = h.name || h.custom_name || "Holiday";
      markers[d] = { type: "holiday", label: h.is_optional ? `${name} (Optional)` : name };
    }
    for (const req of requests || []) {
      if (!["approved", "pending", "escalated"].includes(req.status) || !req.start_date || !req.end_date) continue;
      for (let d = new Date(req.start_date); d <= new Date(req.end_date); d.setDate(d.getDate() + 1)) {
        markers[toDateValue(d)] = { type: "leave", label: req.leave_type_name || req.leave_type_code || "Leave" };
      }
    }
    return markers;
  }, [holidays, leaveModuleHolidays, requests, weekOffPolicy, year]);
}

const STATUS_STYLE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  escalated: "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  withdrawn: "bg-slate-100 text-slate-600 border-slate-200",
};

/** Latest leave requests with their status. */
export function LeaveRequestsTable({ requests, limit = 5, emptyText }) {
  const recent = [...(requests || [])]
    .sort((a, b) => String(b.applied_at || b.start_date).localeCompare(String(a.applied_at || a.start_date)))
    .slice(0, limit);
  if (recent.length === 0) return <p className="text-sm text-slate-500">{emptyText}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[520px]">
        <thead>
          <tr className="text-left text-slate-600">
            <th className="py-2 pr-3 font-semibold">Leave Type</th>
            <th className="py-2 pr-3 font-semibold">From</th>
            <th className="py-2 pr-3 font-semibold">To</th>
            <th className="py-2 pr-3 font-semibold">Days</th>
            <th className="py-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {recent.map((r) => (
            <tr key={r.request_id} className="border-t border-slate-100">
              <td className="py-2 pr-3">{r.leave_type_name || r.leave_type_code || "—"}</td>
              <td className="py-2 pr-3">{formatIndianDate(r.start_date)}</td>
              <td className="py-2 pr-3">{formatIndianDate(r.end_date)}</td>
              <td className="py-2 pr-3">{Number(r.total_days || 0)}</td>
              <td className="py-2">
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-semibold capitalize ${
                    STATUS_STYLE[r.status] || STATUS_STYLE.cancelled
                  }`}
                >
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
